import { useMemo } from 'react';
import { useQuery } from 'urql';

import { REFERENCE_LISTS_QUERY, TaxonomyType } from '@revelio/data-access';

import { RICS_FULL_TAXONOMY_TREE_LEVELS, RICS_TAXONOMY_ID } from '../constants';
import type { GqlSelectionListKey, SelectionListState } from '../types';
import { normalizeReferenceLists } from '../utils/referenceLists';
import { treeToParentLinkedSelectionLists } from '../utils/treeToParentLinkedSelectionLists';
import { useGeographyTaxonomyTree } from './useGeographyTaxonomyTree';
import { useTaxonomyTree } from './useTaxonomyTree';

const RICS_KEYS: readonly GqlSelectionListKey[] = [
  'rics_k10',
  'rics_k50',
  'rics_k400',
  'company',
];

const GEO_KEYS: readonly GqlSelectionListKey[] = [
  'region',
  'country',
  'msa',
  'metro_area',
  'city',
  'state',
  'county',
];

const MSA_ONLY_GEO_KEYS: readonly GqlSelectionListKey[] = ['msa', 'metro_area'];
const SHARED_GEO_KEYS: readonly GqlSelectionListKey[] = [
  'region',
  'country',
  'city',
];

const DEMOGRAPHIC_KEYS: readonly GqlSelectionListKey[] = [
  'seniority',
  'ethnicity',
  'gender',
  'highest_degree',
  'employment_relationship',
  'work_schedule',
  'remote_type',
];

const GEO_TAXONOMY_ID_MSA = 1;
const GEO_TAXONOMY_ID_ADMIN = 2;
/**
 * Routes a list of GqlSelectionListKey values to the appropriate data sources
 * (RICS taxonomy tree, geography taxonomy trees, reference lists) and assembles
 * a single result object containing only the requested keys.
 *
 * Two geo trees exist (level tags as returned by the backend):
 *   - taxonomyId 1 (MSA):   region → country → metro_area → city_core
 *   - taxonomyId 2 (admin): region → country → state → county
 *
 * When both `state` and `metro_area`/`msa` appear in keys, both trees are
 * fetched simultaneously so every column can be populated.
 *
 * Hooks are called unconditionally; `pause` is used to skip inactive fetches.
 */
export const useSelectionListsForKeys = (
  keys: GqlSelectionListKey[],
  opts?: {
    /**
     * Priority for the RICS tree fetch. Defaults to `low` (a filter-UI tree).
     * The adaptive-taxonomy resolution passes `high` because the composition
     * query is paused on it — see `useTaxonomyTree`'s `priority`.
     */
    ricsPriority?: 'high' | 'low';
  }
): {
  selectionLists: SelectionListState<GqlSelectionListKey>;
  fetching: boolean;
} => {
  // Stabilise on the sorted key string so callers can pass an inline array
  // without re-running the assembly memo on every render.
  const keysKey = [...keys].sort().join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the sorted key string above
  const stableKeys = useMemo(() => keys, [keysKey]);

  const hasRicsKeys = stableKeys.some((k) => RICS_KEYS.includes(k));
  const hasDemographicKeys = stableKeys.some((k) =>
    DEMOGRAPHIC_KEYS.includes(k)
  );
  const needsAdminGeo =
    stableKeys.includes('state') || stableKeys.includes('county');
  const needsMsaGeo =
    stableKeys.some((k) => MSA_ONLY_GEO_KEYS.includes(k)) ||
    (stableKeys.some((k) => SHARED_GEO_KEYS.includes(k)) && !needsAdminGeo);

  const {
    treeData: ricsTreeData,
    levels: ricsLevels,
    fetching: ricsFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: RICS_FULL_TAXONOMY_TREE_LEVELS,
    pause: !hasRicsKeys,
    priority: opts?.ricsPriority ?? 'low',
  });

  const {
    treeData: msaTreeData,
    levels: msaLevels,
    fetching: msaFetching,
  } = useGeographyTaxonomyTree({
    taxonomyId: GEO_TAXONOMY_ID_MSA,
    pause: !needsMsaGeo,
  });

  const {
    treeData: adminTreeData,
    levels: adminLevels,
    fetching: adminFetching,
  } = useGeographyTaxonomyTree({
    taxonomyId: GEO_TAXONOMY_ID_ADMIN,
    pause: !needsAdminGeo,
  });

  const [{ data: refData, fetching: refFetching }] = useQuery({
    query: REFERENCE_LISTS_QUERY,
    pause: !hasDemographicKeys,
  });
  const referenceLists = useMemo(
    () => normalizeReferenceLists(refData),
    [refData]
  );

  const selectionLists = useMemo<
    SelectionListState<GqlSelectionListKey>
  >(() => {
    // Internal assembly is heterogeneous (parent-linked tree items, reference
    // lists, legacy lists) so it builds loosely; the single assertion at the
    // return is the one place generic tree items become typed per-key lists.
    const result: Partial<Record<GqlSelectionListKey, unknown[]>> = {};

    if (hasRicsKeys && ricsTreeData.length > 0) {
      const ricsAll = treeToParentLinkedSelectionLists(
        ricsTreeData,
        ricsLevels
      );
      for (const key of stableKeys) {
        if (RICS_KEYS.includes(key) && ricsAll[key]) {
          result[key] = ricsAll[key];
        }
      }
    }

    const msaGeo: Partial<Record<string, unknown[]>> =
      needsMsaGeo && msaTreeData.length > 0
        ? treeToParentLinkedSelectionLists(msaTreeData, msaLevels)
        : {};

    const adminGeo: Partial<Record<string, unknown[]>> =
      needsAdminGeo && adminTreeData.length > 0
        ? treeToParentLinkedSelectionLists(adminTreeData, adminLevels)
        : {};

    for (const key of stableKeys) {
      if (!GEO_KEYS.includes(key)) continue;

      if (key === 'state') {
        if (adminGeo.state) result[key] = adminGeo.state;
      } else if (key === 'county') {
        // County exists only on the administrative tree.
        if (adminGeo.county) result[key] = adminGeo.county;
      } else if (key === 'msa' || key === 'metro_area') {
        // The geo taxonomy tree tags this level `metro_area`; older fixtures
        // used `msa`, so accept either.
        const value = msaGeo.metro_area ?? msaGeo.msa;
        if (value) result[key] = value;
      } else if (key === 'city') {
        // The geo taxonomy tree tags the city level `city_core`; older
        // fixtures used `city`, so accept either, preferring the msa tree.
        const value =
          msaGeo.city_core ??
          adminGeo.city_core ??
          msaGeo.city ??
          adminGeo.city;
        if (value) result[key] = value;
      } else {
        // region / country: prefer msa tree; fall back to admin tree
        const value = msaGeo[key] ?? adminGeo[key];
        if (value) result[key] = value;
      }
    }

    if (referenceLists) {
      for (const key of stableKeys) {
        if (
          DEMOGRAPHIC_KEYS.includes(key) &&
          referenceLists[key as keyof typeof referenceLists]
        ) {
          result[key] = referenceLists[key as keyof typeof referenceLists];
        }
      }
    }

    return result as SelectionListState<GqlSelectionListKey>;
  }, [
    stableKeys,
    hasRicsKeys,
    ricsTreeData,
    ricsLevels,
    needsMsaGeo,
    msaTreeData,
    msaLevels,
    needsAdminGeo,
    adminTreeData,
    adminLevels,
    referenceLists,
  ]);

  const fetching =
    (hasRicsKeys && ricsFetching) ||
    (needsMsaGeo && msaFetching) ||
    (needsAdminGeo && adminFetching) ||
    (hasDemographicKeys && refFetching);

  return { selectionLists, fetching };
};
